/* =====================================================================================================================
   THÈME CONVERSION JEY · comportements de la couche JEY
   Chargé en « defer », sans dépendance, indépendant du moteur du thème (theme.js n'est pas modifié).
   Tout est progressif : sans ce script, le site reste entièrement utilisable et rien n'est masqué.
   ===================================================================================================================== */
(() => {
  'use strict';

  const root = document.documentElement;
  const inEditor = () => window.Shopify && window.Shopify.designMode;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* -------------------------------------------------------------------------------------------------------------------
     1. Apparition des sections au défilement
     La classe .jey-reveal n'est posée que sur des éléments situés sous la ligne de flottaison : aucun impact sur le LCP,
     aucun flash. Désactivé dans l'éditeur de thème et si le visiteur réduit les animations.
     ------------------------------------------------------------------------------------------------------------------- */
  const REVEAL_TARGETS = [
    '.shopify-section:not(.shopify-section-group-header-group):not(.shopify-section-group-overlay-group) .section-header',
    '.shopify-section:not(.shopify-section-group-header-group):not(.shopify-section-group-overlay-group) .section-stack > :not(.section-header)',
    '.jey-section [data-jey-reveal-item]'
  ].join(',');

  function setupReveal() {
    const mode = root.getAttribute('data-jey-reveal');
    if (!mode || mode === 'none' || inEditor() || reducedMotion.matches || !('IntersectionObserver' in window)) return;

    const stagger = mode === 'fade-up-stagger';
    const fold = window.innerHeight * 0.92;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('jey-in');
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0 });

    /* Première passe : uniquement des lectures de mise en page. Deuxième passe : uniquement des écritures. */
    const targets = [];
    document.querySelectorAll(REVEAL_TARGETS).forEach((el) => {
      if (el.classList.contains('jey-reveal') || el.closest('[reveal-on-scroll]')) return;
      if (!el.matches('[data-jey-reveal-item]') && el.querySelector('[data-jey-reveal-item]')) return;
      if (el.getBoundingClientRect().top < fold) return;
      targets.push(el);
    });

    targets.forEach((el) => {
      if (el.parentElement && el.parentElement.closest('.jey-reveal')) return;
      if (stagger && el.parentElement) {
        const index = Array.prototype.indexOf.call(el.parentElement.children, el);
        el.style.setProperty('--jey-reveal-delay', Math.min(index, 5) * 70 + 'ms');
      }
      el.classList.add('jey-reveal');
      observer.observe(el);
    });
  }

  /* -------------------------------------------------------------------------------------------------------------------
     2. Ajustement de texte à la largeur (signature géante du pied de page)
     ------------------------------------------------------------------------------------------------------------------- */
  function fitText(el) {
    const parent = el.parentElement;
    if (!parent) return;
    el.style.fontSize = '';
    const style = getComputedStyle(parent);
    const available = parent.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
    const current = el.getBoundingClientRect().width;
    if (!available || !current) return;
    const size = parseFloat(getComputedStyle(el).fontSize);
    el.style.fontSize = Math.max(24, Math.floor(size * (available / current) * 0.995)) + 'px';
  }

  const runFitText = () => document.querySelectorAll('[data-jey-fit]').forEach(fitText);

  function setupFitText() {
    runFitText();
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(runFitText);
  }

  /* -------------------------------------------------------------------------------------------------------------------
     3. <jey-copy> : copie d'un code promo au clic
     ------------------------------------------------------------------------------------------------------------------- */
  if (!customElements.get('jey-copy')) {
    customElements.define('jey-copy', class extends HTMLElement {
      connectedCallback() {
        this.button = this.querySelector('button');
        if (!this.button) return;
        this.button.addEventListener('click', () => this.copy());
      }

      async copy() {
        const value = this.getAttribute('data-value') || '';
        let copied = true;
        try {
          await navigator.clipboard.writeText(value);
        } catch (error) {
          const field = document.createElement('textarea');
          field.value = value;
          field.setAttribute('readonly', '');
          field.style.cssText = 'position:absolute;left:-9999px';
          document.body.appendChild(field);
          field.select();
          try { copied = document.execCommand('copy'); } catch (e) { copied = false; }
          field.remove();
          this.button.focus({ preventScroll: true });
        }
        if (!copied) return;
        this.classList.add('is-copied');
        const status = this.querySelector('[data-jey-copy-status]');
        if (status) status.textContent = this.getAttribute('data-copied-label') || '';
        clearTimeout(this.timer);
        this.timer = setTimeout(() => {
          this.classList.remove('is-copied');
          if (status) status.textContent = '';
        }, 2200);
      }
    });
  }

  /* -------------------------------------------------------------------------------------------------------------------
     4. <jey-count> : compteur animé (section « Chiffres clés »). Le texte final est déjà dans le HTML (SEO, sans JS).
     ------------------------------------------------------------------------------------------------------------------- */
  if (!customElements.get('jey-count')) {
    customElements.define('jey-count', class extends HTMLElement {
      connectedCallback() {
        if (inEditor() || reducedMotion.matches || !('IntersectionObserver' in window)) return;
        const text = this.textContent.trim();
        const match = text.match(/^(\D*?)(\d(?:[\d\s.,\u202f\u00a0]*\d)?)(.*)$/);
        if (!match) return;

        /* « 2 000 », « 2.000 », « 2,000 » : groupes de milliers. « 4,8 » ou « 4.85 » : décimales. */
        const raw = match[2];
        const decimalMatch = raw.match(/[.,](\d{1,2})$/);
        const decimals = decimalMatch ? decimalMatch[1].length : 0;
        const separator = decimalMatch ? raw.charAt(raw.length - decimals - 1) : '';
        const intPart = decimals ? raw.slice(0, -(decimals + 1)) : raw;
        const grouping = (intPart.match(/\d([\s.,\u202f\u00a0])\d{3}/) || [])[1] || '';
        const target = parseFloat(intPart.replace(/[\s.,\u202f\u00a0]/g, '') + (decimals ? '.' + decimalMatch[1] : ''));
        if (!isFinite(target)) return;
        /* Une année (« Depuis 2019 ») ne se compte pas */
        if (!grouping && !decimals && /^\d{4}$/.test(raw) && target >= 1900 && target <= 2100) return;

        const format = (value) => {
          let [int, dec] = value.toFixed(decimals).split('.');
          if (grouping) int = int.replace(/\B(?=(\d{3})+(?!\d))/g, grouping);
          return match[1] + int + (decimals ? separator + dec : '') + match[3];
        };

        this.style.fontVariantNumeric = 'tabular-nums';
        this.style.display = 'inline-block';
        this.style.minWidth = Math.ceil(this.getBoundingClientRect().width) + 'px';
        const observer = new IntersectionObserver((entries) => {
          if (!entries[0].isIntersecting) return;
          observer.disconnect();
          const duration = 1400;
          const start = performance.now();
          const tick = (now) => {
            const progress = Math.min(1, (now - start) / duration);
            const eased = 1 - Math.pow(1 - progress, 3);
            this.textContent = format(target * eased);
            if (progress < 1) requestAnimationFrame(tick);
            else this.textContent = text;
          };
          requestAnimationFrame(tick);
        }, { threshold: 0.4 });
        observer.observe(this);
      }
    });
  }

  /* -------------------------------------------------------------------------------------------------------------------
     5. <jey-video-wall> : vidéos verticales. Une seule lecture avec le son à la fois, pause hors écran.
     ------------------------------------------------------------------------------------------------------------------- */
  if (!customElements.get('jey-video-wall')) {
    customElements.define('jey-video-wall', class extends HTMLElement {
      connectedCallback() {
        if ('IntersectionObserver' in window) {
          this.observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting && !entry.target.paused) entry.target.pause();
            });
          }, { threshold: 0.2 });
        }

        this.addEventListener('click', (event) => {
          const toggle = event.target.closest('[data-jey-video-toggle]');
          if (!toggle || !this.contains(toggle)) return;
          const card = toggle.closest('[data-jey-video]');
          if (!card) return;
          event.preventDefault();

          const video = card.querySelector('video') || this.mount(card);
          if (!video) return;

          this.querySelectorAll('[data-jey-video].is-playing video').forEach((other) => {
            if (other !== video) other.pause();
          });
          video.muted = false;
          const attempt = video.play();
          if (attempt && attempt.catch) attempt.catch(() => { video.muted = true; video.play().catch(() => {}); });
          video.focus({ preventScroll: true });
        });
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
      }

      /* La vidéo attend dans un <template> : elle n'est créée, donc téléchargée, qu'au premier clic */
      mount(card) {
        const template = card.querySelector('template[data-jey-video-template]');
        const frame = card.querySelector('.jey-ugc__frame');
        if (!template || !frame) return null;
        frame.appendChild(template.content.cloneNode(true));
        const video = frame.querySelector('video');
        if (!video) return null;
        const toggle = card.querySelector('[data-jey-video-toggle]');
        video.addEventListener('play', () => { card.classList.add('is-playing'); if (toggle) toggle.hidden = true; });
        video.addEventListener('pause', () => { card.classList.remove('is-playing'); if (toggle) toggle.hidden = false; });
        video.addEventListener('ended', () => { video.currentTime = 0; });
        if (this.observer) this.observer.observe(video);
        return video;
      }
    });
  }

  /* -------------------------------------------------------------------------------------------------------------------
     6. <jey-countdown> : même élément que le bloc produit (snippets/jey-countdown.liquid), défini ici pour les sections.
     Date de fin réelle uniquement : l'élément se retire de lui-même une fois la date passée.
     ------------------------------------------------------------------------------------------------------------------- */
  if (!customElements.get('jey-countdown')) {
    customElements.define('jey-countdown', class extends HTMLElement {
      connectedCallback() {
        this.end = new Date(this.getAttribute('data-end')).getTime();
        if (isNaN(this.end)) return;
        this.els = {
          d: this.querySelector('[data-days]'), h: this.querySelector('[data-hours]'),
          m: this.querySelector('[data-minutes]'), s: this.querySelector('[data-seconds]')
        };
        this.dayUnit = this.els.d ? this.els.d.parentElement : null;
        this.tick();
        this.timer = setInterval(() => this.tick(), 1000);
      }

      disconnectedCallback() { clearInterval(this.timer); }

      tick() {
        const diff = this.end - Date.now();
        if (diff <= 0) {
          clearInterval(this.timer);
          const host = this.closest('[data-jey-countdown-host]');
          (host || this).remove();
          return;
        }
        const pad = (n) => String(n).padStart(2, '0');
        const d = Math.floor(diff / 86400000);
        if (this.dayUnit) this.dayUnit.hidden = d === 0;
        if (this.els.d) this.els.d.textContent = pad(d);
        if (this.els.h) this.els.h.textContent = pad(Math.floor(diff / 3600000) % 24);
        if (this.els.m) this.els.m.textContent = pad(Math.floor(diff / 60000) % 60);
        if (this.els.s) this.els.s.textContent = pad(Math.floor(diff / 1000) % 60);
        this.hidden = false;
      }
    });
  }

  /* -------------------------------------------------------------------------------------------------------------------
     7. Bandeaux défilants : bouton pause (exigence d'accessibilité : tout ce qui bouge plus de 5 s doit pouvoir s'arrêter)
     ------------------------------------------------------------------------------------------------------------------- */
  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-jey-marquee-toggle]');
    if (!toggle) return;
    const paused = toggle.getAttribute('aria-pressed') !== 'true';
    const host = toggle.closest('.jey-section');
    const label = toggle.querySelector('[data-jey-marquee-label]');
    toggle.setAttribute('aria-pressed', String(paused));
    if (host) host.classList.toggle('is-marquee-paused', paused);
    if (label) label.textContent = toggle.getAttribute(paused ? 'data-label-play' : 'data-label-pause') || label.textContent;
  });

  /* -------------------------------------------------------------------------------------------------------------------
     8. Vidéo de fond du hero : bouton pause, et aucune lecture automatique si le visiteur réduit les animations
     ------------------------------------------------------------------------------------------------------------------- */
  function setupBackgroundVideos(scope) {
    (scope || document).querySelectorAll('.content-over-media').forEach((media) => {
      const videos = media.querySelectorAll(':scope > .hero-promise__bg-video');
      if (!videos.length || media.querySelector(':scope > .jey-bg-video-toggle')) return;

      if (reducedMotion.matches) videos.forEach((video) => { video.removeAttribute('autoplay'); video.pause(); });

      const labels = [media.getAttribute('data-jey-video-pause') || 'Pause video', media.getAttribute('data-jey-video-play') || 'Play video'];
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'jey-bg-video-toggle';
      button.innerHTML = '<svg aria-hidden="true" focusable="false" width="12" height="12" viewBox="0 0 12 12"><path class="jey-bg-video-toggle__pause" fill="currentColor" d="M2 1h3v10H2zM7 1h3v10H7z"/><path class="jey-bg-video-toggle__play" fill="currentColor" d="M3 1l8 5-8 5z"/></svg>';

      const sync = (paused) => {
        button.setAttribute('aria-pressed', String(paused));
        button.setAttribute('aria-label', labels[paused ? 1 : 0]);
      };
      sync(reducedMotion.matches);

      button.addEventListener('click', () => {
        const pause = button.getAttribute('aria-pressed') !== 'true';
        videos.forEach((video) => {
          if (pause) video.pause();
          else { const attempt = video.play(); if (attempt && attempt.catch) attempt.catch(() => {}); }
        });
        sync(pause);
      });
      media.append(button);
    });
  }

  /* -------------------------------------------------------------------------------------------------------------------
     9. Zones que l'on fait défiler (réassurance sur mobile) : atteignables au clavier dès qu'elles débordent
     ------------------------------------------------------------------------------------------------------------------- */
  function syncScrollers() {
    document.querySelectorAll('.jey-trust__list').forEach((el) => {
      if (el.scrollWidth > el.clientWidth + 1) el.setAttribute('tabindex', '0');
      else el.removeAttribute('tabindex');
    });
  }

  /* -------------------------------------------------------------------------------------------------------------------
     Démarrage
     ------------------------------------------------------------------------------------------------------------------- */
  function init() {
    setupReveal();
    setupFitText();
    setupBackgroundVideos();
    syncScrollers();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  let resizeFrame;
  window.addEventListener('resize', () => {
    cancelAnimationFrame(resizeFrame);
    resizeFrame = requestAnimationFrame(() => { runFitText(); syncScrollers(); });
  }, { passive: true });

  /* Éditeur de thème : une section rechargée retrouve ses comportements, le bloc sélectionné vient à l'écran */
  document.addEventListener('shopify:section:load', (event) => {
    setupFitText();
    setupBackgroundVideos(event.target);
    syncScrollers();
  });

  document.addEventListener('shopify:block:select', (event) => {
    const track = event.target.closest('.jey-marquee__track');
    if (track) track.style.animationPlayState = 'paused';
    else if (event.target.closest('.jey-section')) event.target.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
  });

  document.addEventListener('shopify:block:deselect', (event) => {
    const track = event.target.closest('.jey-marquee__track');
    if (track) track.style.animationPlayState = '';
  });
})();
